import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ErrorBody } from './types.js';

// Augment axios request config with our flags
declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Mark as already confirmed — bypass global interceptor confirm */
    confirmed?: boolean;
    /** Optional contextual confirm message (used by useConfirmedMutation) */
    confirmMessage?: { title: string; message: string; destructive?: boolean };
  }
  export interface InternalAxiosRequestConfig {
    confirmed?: boolean;
    confirmMessage?: { title: string; message: string; destructive?: boolean };
  }
}

type GlobalConfirm = (opts: { title: string; message: string; destructive?: boolean }) => Promise<boolean>;
type SessionHandler = {
  on401: () => void;
  onMustChangePassword: () => void;
  onLocked: (retryAfterSeconds: number) => void;
  onError: (message: string) => void;
};

let globalConfirm: GlobalConfirm = async () => true;
let sessionHandler: SessionHandler = {
  on401: () => {},
  onMustChangePassword: () => {},
  onLocked: () => {},
  onError: () => {},
};

export function setGlobalConfirm(fn: GlobalConfirm): void {
  globalConfirm = fn;
}

export function getGlobalConfirm(): GlobalConfirm {
  return globalConfirm;
}

export function setSessionHandler(h: SessionHandler): void {
  sessionHandler = h;
}

export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 15_000,
});

// Request interceptor — confirmation gate
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (config.confirmed === true) return config;
  const m = (config.method ?? 'get').toUpperCase();
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return config;
  const dialog = config.confirmMessage ?? {
    title: `${m} ${config.url ?? '?'}`,
    message: `Confirm this operation?\n\n${JSON.stringify(config.data ?? {}, null, 2)}`,
  };
  const ok = await globalConfirm(dialog);
  if (!ok) throw new axios.Cancel('User cancelled');
  return config;
});

// Response interceptor — global error routing
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (axios.isCancel(err)) {
      return Promise.reject({ code: 'ERR_CANCELED', message: 'User cancelled' });
    }
    const status: number | undefined = err.response?.status;
    const body: ErrorBody | undefined = err.response?.data;

    if (status === 401) sessionHandler.on401();
    else if (status === 403 && body?.error === 'MustChangePassword') sessionHandler.onMustChangePassword();
    else if (status === 429 && body?.error === 'Locked') sessionHandler.onLocked(body.retry_after_seconds ?? 60);
    else sessionHandler.onError(body?.message ?? body?.error ?? err.message);

    return Promise.reject(err);
  },
);
